/** Resolve a media URL the same way the browser does on <audio src>. */
export function resolveBrowserMediaUrl(src: string): string {
  const trimmed = src.trim();
  if (!trimmed) return '';
  try {
    return new URL(trimmed, window.location.href).href;
  } catch {
    return trimmed;
  }
}

/** True when the audio element already points at the same resource as `src`. */
export function mediaSrcMatches(audioSrc: string, propSrc: string): boolean {
  if (!propSrc.trim()) return !audioSrc;
  if (!audioSrc) return false;
  const resolved = resolveBrowserMediaUrl(propSrc);
  return audioSrc === propSrc || audioSrc === resolved;
}

/** True when `src` resolves to a different origin than the current page. */
export function isCrossOriginMediaUrl(src: string): boolean {
  const trimmed = src.trim();
  if (!trimmed || trimmed.startsWith('blob:') || trimmed.startsWith('data:')) return false;
  if (typeof window === 'undefined') return false;
  try {
    return new URL(trimmed, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Set crossOrigin only when the resource is actually cross-origin.
 * Same-origin media should stay in non-CORS mode so a misconfigured
 * `Access-Control-Allow-Origin: *` + credentials combo cannot break playback.
 */
export function applyAudioCrossOrigin(audio: HTMLAudioElement, src: string): void {
  const trimmed = src.trim();
  if (!trimmed || trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    if (audio.crossOrigin) audio.crossOrigin = null;
    return;
  }

  if (isCrossOriginMediaUrl(trimmed)) {
    if (audio.crossOrigin !== 'anonymous') {
      audio.crossOrigin = 'anonymous';
    }
  } else if (audio.crossOrigin) {
    audio.crossOrigin = null;
  }
}

/** Transient / race-prone media errors that often succeed on reload. */
export function isRetryableMediaError(error: MediaError | null | undefined): boolean {
  if (!error) return true;
  return (
    error.code === MediaError.MEDIA_ERR_ABORTED ||
    error.code === MediaError.MEDIA_ERR_NETWORK ||
    error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
  );
}

export const AUDIO_LOAD_MAX_ATTEMPTS = 3;

/** Delay before retry attempt `attempt` (1-based after the first failure). */
export function audioLoadRetryDelayMs(attempt: number): number {
  return Math.min(120 * attempt, 400);
}

/** Wait until the element can play or fail definitively. */
export function waitForAudioCanPlay(
  audio: HTMLAudioElement,
  timeoutMs = 15000
): Promise<void> {
  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('error', onError);
      fn();
    };

    const onReady = () => finish(resolve);
    const onError = () => {
      const code = audio.error?.code;
      const message =
        code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
          ? 'The element has no supported sources.'
          : audio.error?.message || 'Audio failed to load';
      finish(() => reject(new Error(message)));
    };

    const timer = window.setTimeout(() => {
      finish(() => reject(new Error('Audio load timed out')));
    }, timeoutMs);

    audio.addEventListener('canplay', onReady);
    audio.addEventListener('error', onError);

    if (audio.networkState === HTMLMediaElement.NETWORK_EMPTY && audio.src) {
      audio.load();
    }
  });
}

/** Play with a single reload retry when the source was not ready yet. */
export async function safeAudioPlay(audio: HTMLAudioElement): Promise<void> {
  if (!audio.src?.trim()) {
    throw new Error('The element has no supported sources.');
  }

  try {
    await waitForAudioCanPlay(audio);
    await audio.play();
  } catch (firstError) {
    if (!audio.src?.trim()) throw firstError;
    audio.load();
    await waitForAudioCanPlay(audio);
    await audio.play();
  }
}
